import React, { useCallback, useEffect, useState } from "react";
import { Alert, Box, Chip, Collapse, Tooltip, Typography } from "@mui/material";
import ButtonOne from "../../common/normal-button";
import AppTable from "../../common/app-table";
import typography from "../../cssUtils/typography";
import { useAuth } from "../../hooks/AuthProvider";
import { EMI_REMINDERS_UPLOAD_ENDPOINT, EMI_REMINDERS_UPLOAD_LOGS_ENDPOINT } from "../../config/urls";
import { formatWithTzLabel } from "../../utils/dateUtils";

const SAMPLE_FILE = { url: "/emi_reminder_sample.csv", filename: "emi_reminder_sample.csv" };

const FILE_FORMAT_HELP = [
  "CSV file with fixed columns: Name, Loan A/C Number, Country Code, Phone Number, Due Date, Payment Amount.",
  "Mandatory values: Name, Phone Number, Due Date. Country Code defaults to 91 if blank; Loan A/C Number and Payment Amount are optional.",
  "Due Date is the EMI's day of the month — digits only, 1 to 31.",
  "Optional column: Is Active — Yes or No (any case) sets the customer's Active status; blank or any other value is treated as N/A and leaves it unchanged (new customers are active). Files without this column are still accepted.",
  "Country Code + Phone Number identifies the customer: an existing customer's record is updated with the uploaded values, otherwise a new record is created.",
];

const LOG_COLUMNS = [
  { id: "file_name", label: "File Name", minWidth: 200 },
  { id: "uploaded_by_name", label: "Uploaded By", minWidth: 150 },
  {
    id: "created_at",
    label: "Uploaded At",
    minWidth: 170,
    render: (row) => formatWithTzLabel(row.created_at),
  },
  {
    id: "status",
    label: "Status",
    minWidth: 100,
    render: (row) =>
      row.status === "success" ? (
        <Chip variant="outlined" color="success" label="Success" size="small" />
      ) : (
        <Chip variant="outlined" color="error" label="Failed" size="small" />
      ),
  },
  {
    id: "message",
    label: "Message",
    minWidth: 260,
    // The table's cells never wrap, so cap the width and clamp long errors to a
    // few lines — the full text is in the tooltip.
    render: (row) =>
      row.message ? (
        <Tooltip title={row.message} placement="top-start" arrow>
          <Typography
            sx={{
              ...typography.bodyLarge,
              maxWidth: 360,
              whiteSpace: "normal",
              wordBreak: "break-word",
              display: "-webkit-box",
              WebkitLineClamp: 3,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {row.message}
          </Typography>
        </Tooltip>
      ) : (
        "-"
      ),
  },
];

const RowIssues = ({ title, rows, color }) => {
  if (!rows?.length) return null;
  return (
    <Box sx={{ mt: 2, width: "100%", maxWidth: 600 }}>
      <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
        {title} ({rows.length})
      </Typography>
      <Box sx={{ maxHeight: 280, overflowY: "auto", border: "1px solid #E5E7EB", borderRadius: 1, p: 1.5 }}>
        {rows.map(({ row, errors }) => (
          <Box
            key={row}
            sx={{ mb: 1, pb: 1, borderBottom: "1px solid #F3F4F6", "&:last-child": { mb: 0, pb: 0, borderBottom: "none" } }}
          >
            <Typography variant="caption" fontWeight={700} color={color}>
              Row {row}
            </Typography>
            {errors.map((msg, i) => (
              <Typography key={i} variant="caption" display="block" color="text.secondary">
                &bull; {msg}
              </Typography>
            ))}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const EMIUploadTab = () => {
  const { getValidToken } = useAuth();

  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertStatus, setAlertStatus] = useState("success");
  const [alertMessage, setAlertMessage] = useState("");

  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);

  useEffect(() => {
    if (!alertOpen) return;
    const id = setTimeout(() => setAlertOpen(false), 8000);
    return () => clearTimeout(id);
  }, [alertOpen]);

  const showAlert = (severity, message) => {
    setAlertStatus(severity);
    setAlertMessage(message);
    setAlertOpen(true);
  };

  const fetchLogs = useCallback(async () => {
    setLogsLoading(true);
    try {
      const token = await getValidToken();
      const tenant = localStorage.getItem("tenant");
      const params = new URLSearchParams({ limit: 10, offset: 0 });
      const res = await fetch(`${EMI_REMINDERS_UPLOAD_LOGS_ENDPOINT}?${params}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "X-Tenant-Id": tenant,
        },
      });
      if (!res.ok) throw new Error("Failed to fetch upload logs");
      const data = await res.json();
      setLogs(data.results ?? []);
    } catch (err) {
      console.error("Error fetching EMI upload logs:", err);
    } finally {
      setLogsLoading(false);
    }
  }, [getValidToken]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handleFileChange = (e) => {
    const selected = e.target.files[0] ?? null;
    setResult(null);
    setAlertOpen(false);
    if (selected && !selected.name.toLowerCase().endsWith(".csv")) {
      resetFileInput();
      showAlert("error", "Invalid file type. Only CSV (.csv) files are supported.");
      return;
    }
    setFile(selected);
  };

  const resetFileInput = () => {
    setFile(null);
    const el = document.getElementById("emi-reminder-file-input");
    if (el) el.value = "";
  };

  const handleUpload = async () => {
    if (!file) {
      showAlert("error", "Please select a file to upload.");
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      const token = await getValidToken();
      const tenant = localStorage.getItem("tenant");

      const formData = new FormData();
      formData.append("file", file);

      const res = await fetch(EMI_REMINDERS_UPLOAD_ENDPOINT, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "X-Tenant-Id": tenant,
        },
        body: formData,
      });

      const data = await res.json();

      if (!res.ok) {
        showAlert("error", data.error || "Upload failed.");
        resetFileInput();
        fetchLogs();
        return;
      }

      setResult(data);
      const hasErrors = data.errors?.length > 0;
      const hasSuccess = data.inserted > 0 || data.updated > 0;
      const savedNote = `${data.inserted} created, ${data.updated} updated.`;

      if (!hasErrors) {
        showAlert("success", `Upload complete: ${savedNote}`);
      } else if (hasSuccess) {
        showAlert("warning", `Partial success: ${savedNote} ${data.failed} row(s) failed.`);
      } else {
        showAlert("error", `Upload failed — ${data.failed} row(s) had errors.`);
      }

      resetFileInput();
      fetchLogs();
    } catch (err) {
      console.error("Upload error:", err);
      showAlert("error", "An unexpected error occurred.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
      <Box sx={{ mb: 3, width: "100%" }}>
        {FILE_FORMAT_HELP.map((line) => (
          <Typography key={line} variant="caption" color="text.secondary" display="block">
            &bull; {line}
          </Typography>
        ))}
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Download sample:{" "}
          <Typography
            variant="body2"
            component="a"
            href={SAMPLE_FILE.url}
            download={SAMPLE_FILE.filename}
            sx={{
              textDecoration: "none",
              cursor: "pointer",
              color: "primary.main",
              fontWeight: "bold",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            EMI Reminder CSV
          </Typography>
        </Typography>
      </Box>

      <Box sx={{ mb: 2 }}>
        <Typography sx={{ ...typography.bodyLarge, mb: 1 }}>Select a file to upload:</Typography>
        <input id="emi-reminder-file-input" type="file" accept=".csv" onChange={handleFileChange} />
      </Box>

      <Collapse in={alertOpen} sx={{ mb: alertOpen ? 1.5 : 0, width: "100%", maxWidth: 600 }}>
        <Alert severity={alertStatus} onClose={() => setAlertOpen(false)}>
          {alertMessage}
        </Alert>
      </Collapse>

      <ButtonOne
        title={loading ? "Uploading…" : "Upload"}
        variant="contained"
        onClick={handleUpload}
        disabled={loading || !file}
      />

      {result && (
        <Box sx={{ mt: 3, width: "100%", maxWidth: 600 }}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
            Upload Summary
          </Typography>
          <Box sx={{ border: "1px solid #E5E7EB", borderRadius: 1, overflow: "hidden" }}>
            {[
              { label: "Total Records", value: result.total ?? "—" },
              { label: "Created", value: result.inserted ?? "—", color: "success.main" },
              { label: "Updated", value: result.updated ?? "—", color: "success.main" },
              { label: "Failed", value: result.failed ?? "—", color: result.failed > 0 ? "error.main" : "text.primary" },
              { label: "Superseded (repeated in file)", value: result.superseded ?? "—" },
              { label: "Skipped (blank rows)", value: result.skipped ?? "—" },
            ].map(({ label, value, color }, i) => (
              <Box
                key={label}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  px: 1.5,
                  py: 0.75,
                  bgcolor: i % 2 === 0 ? "#F9FAFB" : "#FFFFFF",
                }}
              >
                <Typography variant="body2" color="text.secondary">{label}</Typography>
                <Typography variant="body2" fontWeight={600} color={color || "text.primary"}>
                  {typeof value === "number" ? value.toLocaleString() : value}
                </Typography>
              </Box>
            ))}
          </Box>
          {result.audience_sync_failed > 0 && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
              EMI records were saved. Audience sync is pending for {result.audience_sync_failed} record(s) and
              has been logged for reprocessing.
            </Typography>
          )}
        </Box>
      )}

      <RowIssues title="Failure Details" rows={result?.errors} color="error.main" />
      <RowIssues title="Superseded Rows" rows={result?.superseded_rows} color="text.secondary" />

      <Box sx={{ mt: 4, width: "100%" }}>
        <Typography sx={{ ...typography.h2, mb: 1 }}>Recent Uploads</Typography>
        <AppTable
          columns={LOG_COLUMNS}
          rows={logs}
          rowKey="id"
          loading={logsLoading}
          emptyMessage="No uploads yet"
          maxHeight={320}
          hideMainScroll={false}
          containerSx={{ overflowX: "auto", width: "100%" }}
        />
      </Box>
    </Box>
  );
};

export default EMIUploadTab;
